// Container-Editor: ein Bibliotheks-Element aus Bausteinen (Dateien aus der Bibliothek, Stille in Sekunden).
// Reihenfolge per Ziehen, Gesamtlänge automatisch, Vorhören der Abfolge, Titel/Interpret wie ein Song.

import { useEffect, useRef, useState } from "react";
import { api, fmtMs, toast, useLoad } from "../../api";
import { Field, Modal } from "../../ui";

type Part = ({ kind: "media"; media_id: string; title: string; artist?: string; duration_ms: number; cue_in?: number; cue_out?: number; media_kind?: string } | { kind: "silence"; seconds: number; title?: string; duration_ms?: number }) & { meta?: string };
/** What a part reports for the stream metadata (per output it is decided which kinds send their title). */
const META: [string, string][] = [["music", "Musik"], ["jingle", "Jingle"], ["dropper", "Dropper"], ["station_id", "Station-ID"], ["imaging", "Sweeper/Imaging"], ["ad", "Werbung"], ["spoken", "Wort"], ["other", "Stille/Sonstiges"]];
const metaOf = (p: Part) => p.meta || (p.kind === "silence" ? "other" : (p as any).media_kind || "music");

const partLen = (p: Part) => (p.kind === "silence" ? Math.round(p.seconds * 1000) : p.duration_ms);

export function ContainerEditor({ id, onClose }: { id: string | null; onClose: (changed: boolean) => void }) {
  const { data: cats } = useLoad<any[]>("/api/categories");
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [kind, setKind] = useState("music");
  const [owner, setOwner] = useState("auto");
  const [catIds, setCatIds] = useState<string[]>([]);
  const [parts, setParts] = useState<Part[]>([]);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<any[]>([]);
  const [silence, setSilence] = useState("2");
  const [drag, setDrag] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState<number | null>(null);
  const audio = useRef<HTMLAudioElement>(new Audio());
  const stopRef = useRef(false);

  useEffect(() => {
    if (!id) return;
    api(`/api/containers/${id}`).then((c) => {
      setTitle(c.title);
      setArtist(c.artist ?? "");
      setKind(c.kind);
      setOwner(c.owner_station ? "station" : "shared");
      setParts(c.parts);
    }).catch((e) => toast(e.message, true));
  }, [id]);
  useEffect(() => {
    if (!q.trim()) return setHits([]);
    const t = window.setTimeout(async () => setHits((await api(`/api/library?q=${encodeURIComponent(q)}`)).items.slice(0, 10)), 250);
    return () => window.clearTimeout(t);
  }, [q]);
  useEffect(() => () => { stopRef.current = true; audio.current.pause(); }, []);

  const total = parts.reduce((a, p) => a + partLen(p), 0);
  const addMedia = (m: any) => {
    const ci = m.cue_in ?? 0, co = m.cue_out ?? m.duration_ms;
    setParts([...parts, { kind: "media", media_id: m.id, title: m.title, artist: m.artist, duration_ms: Math.max(0, co - ci), cue_in: ci, cue_out: co, media_kind: m.kind }]);
    setQ("");
  };
  const move = (from: number, to: number) => { const n = [...parts]; const [x] = n.splice(from, 1); n.splice(to, 0, x); setParts(n); };

  /** Play the sequence: files from cue in to cue out, silence as a pause. */
  const preview = async (from = 0) => {
    stopRef.current = false;
    const a = audio.current;
    for (let i = from; i < parts.length && !stopRef.current; i++) {
      setPlaying(i);
      const p = parts[i];
      if (p.kind === "silence") {
        await new Promise((r) => setTimeout(r, p.seconds * 1000));
        continue;
      }
      await new Promise<void>((res) => {
        a.src = `/api/media/${p.media_id}/audio`;
        const end = (p.cue_out ?? 0) / 1000;
        const tick = () => { if (stopRef.current || (end > 0 && a.currentTime >= end)) { a.pause(); cleanup(); res(); } };
        const cleanup = () => { a.removeEventListener("timeupdate", tick); a.removeEventListener("ended", done); };
        const done = () => { cleanup(); res(); };
        a.addEventListener("timeupdate", tick);
        a.addEventListener("ended", done);
        a.addEventListener("loadedmetadata", () => { a.currentTime = (p.cue_in ?? 0) / 1000; a.play().catch(() => { cleanup(); res(); }); }, { once: true });
      });
    }
    setPlaying(null);
  };
  const stop = () => { stopRef.current = true; audio.current.pause(); setPlaying(null); };

  const save = async () => {
    setBusy(true);
    try {
      const body = { title, artist, kind, owner: owner === "auto" ? undefined : owner, categories: catIds, parts: parts.map((p) => (p.kind === "silence" ? { kind: "silence", seconds: p.seconds, meta: p.meta || undefined } : { kind: "media", media_id: p.media_id, meta: p.meta || undefined })) };
      if (id) await api(`/api/containers/${id}`, { method: "PUT", body });
      else await api("/api/containers", { body });
      toast("Container gespeichert");
      onClose(true);
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal wide title={id ? `Container „${title}“` : "Neuer Container"} onClose={() => { stop(); onClose(false); }}>
      <div className="form">
        <Field label="Titel (für die Metadaten)"><input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="z. B. Werbung laut.fm" /></Field>
        <Field label="Interpret"><input type="text" value={artist} onChange={(e) => setArtist(e.target.value)} placeholder="z. B. Radio Geretsried" /></Field>
        <Field label="Art"><select value={kind} onChange={(e) => setKind(e.target.value)}><option value="music">Musik</option><option value="jingle">Jingle</option><option value="dropper">Dropper</option><option value="station_id">Station-ID</option><option value="imaging">Imaging</option><option value="ad">Werbung</option><option value="spoken">Wort</option></select></Field>
        <Field label="Gehört zu"><select value={owner} onChange={(e) => setOwner(e.target.value)}><option value="auto">automatisch nach Art</option><option value="shared">allen Sendern</option><option value="station">nur diesem Sender</option></select></Field>
        {!id && (
          <Field label="In Kategorien" full>
            <div className="row wrap" style={{ gap: 4 }}>
              {(cats ?? []).map((c) => <label key={c.id} className="badge" style={{ cursor: "pointer", background: catIds.includes(c.id) ? "var(--accent-2)" : undefined }}><input type="checkbox" checked={catIds.includes(c.id)} onChange={() => setCatIds(catIds.includes(c.id) ? catIds.filter((x) => x !== c.id) : [...catIds, c.id])} /> {c.name}</label>)}
            </div>
          </Field>
        )}
      </div>
      <h3 style={{ marginBottom: 6 }}>Abfolge <span className="small muted">· Gesamtlänge {fmtMs(total)}</span></h3>
      <div style={{ border: "1px solid var(--line)", borderRadius: 8 }}>
        {!parts.length && <div className="small muted" style={{ padding: 10 }}>Noch keine Bausteine. Unten Dateien suchen oder Stille hinzufügen.</div>}
        {parts.map((p, i) => (
          <div key={i} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (drag != null && drag !== i) move(drag, i); setDrag(null); }}
            className="row" style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", background: playing === i ? "#fbe3dc" : p.kind === "silence" ? "#f4f2ec" : undefined, gap: 10 }}>
            <span style={{ cursor: "grab" }} title="Ziehen zum Umsortieren">⠿</span>
            <span className="mono small" style={{ width: 24 }}>{i + 1}.</span>
            {p.kind === "silence" ? (
              <span style={{ flex: 1 }}>⎵ Stille <input type="number" min={0.05} max={60} step={0.05} value={p.seconds} onChange={(e) => setParts(parts.map((x, j) => (j === i ? { kind: "silence", seconds: Number(e.target.value), meta: x.meta } : x)))} style={{ width: 80 }} /> s</span>
            ) : (
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>♪ <b>{p.title}</b>{p.artist ? <span className="muted"> · {p.artist}</span> : null}</span>
            )}
            <select className="small" style={{ width: 130 }} title="Art für die Metadaten: welche Ausgänge beim Start dieses Bausteins seinen Titel senden" value={metaOf(p)}
              onChange={(e) => setParts(parts.map((x, j) => (j === i ? { ...x, meta: e.target.value } : x)))}>
              {META.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <span className="mono small">{fmtMs(partLen(p))}</span>
            <button className="iconbtn" title="Ab hier vorhören" onClick={() => { stop(); setTimeout(() => preview(i), 50); }}>▶</button>
            <button className="iconbtn" title="Entfernen" onClick={() => setParts(parts.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
      </div>
      <div className="row wrap" style={{ marginTop: 8, gap: 8 }}>
        <input type="text" placeholder="Datei aus der Bibliothek suchen (z. B. Ad-Trigger, Jingle) …" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 360 }} />
        <span className="small">Stille</span>
        <input type="number" min={0.05} max={60} step={0.05} value={silence} onChange={(e) => setSilence(e.target.value)} style={{ width: 80 }} />
        <span className="small">s</span>
        <button className="btn small" onClick={() => { const s = Number(silence.replace(",", ".")); if (s > 0) setParts([...parts, { kind: "silence", seconds: s }]); }}>+ Stille</button>
        <span className="spacer" />
        {playing == null ? <button className="btn small" disabled={!parts.length} onClick={() => preview(0)}>▶ Abfolge vorhören</button> : <button className="btn small" onClick={stop}>■ Stopp</button>}
      </div>
      {hits.length > 0 && (
        <div className="small" style={{ marginTop: 4, maxHeight: 200, overflowY: "auto", border: "1px solid var(--line)", borderRadius: 8 }}>
          {hits.map((h) => <div key={h.id} className="row" style={{ padding: "4px 8px", cursor: "pointer" }} onClick={() => addMedia(h)}><span style={{ flex: 1 }}>{h.artist ? `${h.artist} – ` : ""}{h.title}</span><span className="muted">{fmtMs(h.duration_ms)}</span><span>+</span></div>)}
        </div>
      )}
      <p className="small muted">Die Bausteine werden lückenlos zu einer Datei zusammengesetzt; Stille ist echte digitale Stille (die Klangbearbeitung zieht sie nicht hoch). Für die Metadaten meldet sich jeder Baustein beim Start einzeln mit seiner Art (rechts wählbar) – z. B. START_AD_BREAK als „Musik“, damit laut.fm die Werbung auslöst, Stille und Jingle nicht. Welche Arten ein Ausgang sendet, steht beim Ausgang.</p>
      <div className="row" style={{ marginTop: 8 }}>
        <span className="spacer" />
        <button className="btn" onClick={() => { stop(); onClose(false); }}>Abbrechen</button>
        <button className="btn primary" disabled={busy || !title.trim() || !parts.length} onClick={save}>{busy ? "Setze zusammen …" : "Speichern"}</button>
      </div>
    </Modal>
  );
}
